import React from "react";
import { Button, Spinner } from "components/ui/BootstrapCompat";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import { DonateWidget } from "services/main/donate.service";
import type { CreateWidgetFormState, FlowEditorState, WidgetConfigState } from "./types";
import { FlowBuilder } from "./FlowBuilder";
import { TextInput } from "./FormControls";

type WidgetEditorScreenProps = {
  activeWidget?: DonateWidget;
  widgetForm: CreateWidgetFormState;
  flowState: FlowEditorState;
  config: WidgetConfigState;
  selectedFlowNodeId: string;
  isSaveDisabled: boolean;
  isSaving: boolean;
  onBack: () => void;
  onSave: () => void;
  onWidgetFormChange: (patch: Partial<CreateWidgetFormState>) => void;
  onFlowChange: React.Dispatch<React.SetStateAction<FlowEditorState>>;
  onSelectFlowNode: (nodeId: string) => void;
};

export function WidgetEditorScreen({
  activeWidget,
  widgetForm,
  flowState,
  config,
  selectedFlowNodeId,
  isSaveDisabled,
  isSaving,
  onBack,
  onSave,
  onWidgetFormChange,
  onFlowChange,
  onSelectFlowNode,
}: WidgetEditorScreenProps) {
  return (
    <main className="-mx-3 -my-3 flex h-[calc(100%+1.5rem)] min-h-[560px] flex-col overflow-hidden bg-[#e9eef5] sm:-mx-5 sm:-my-4 sm:h-[calc(100%+2rem)] sm:min-h-[680px]">
      <div className="flex flex-none flex-col gap-3 border-b border-[#dbe4ef] bg-white px-4 py-3 shadow-sm lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 gap-3">
          <Button
            variant="light"
            className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-[#dbe4ef] bg-white p-0 text-[#344054]"
            onClick={onBack}
          >
            <FiArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="m-0 truncate text-xl font-black text-[#101828]">
              ປັບແຕ່ງ: {widgetForm.name || activeWidget?.name || "Widget"}
            </h1>
            <p className="m-0 mt-1 truncate text-xs font-medium text-[#667085]">
              {widgetForm.description ||
                activeWidget?.description ||
                "ປັບແຕ່ງ OBS overlay ສຳລັບ widget ທີ່ເລືອກ."}
            </p>
          </div>
        </div>

        <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(180px,260px)_minmax(220px,340px)_auto] sm:items-end">
          <TextInput
            label="ຊື່ Widget"
            value={widgetForm.name}
            onChange={(value) => onWidgetFormChange({ name: value })}
          />
          <label className="block min-w-0">
            <span className="mb-1 block text-xs font-bold text-[#667085]">
              ລາຍລະອຽດ
            </span>
            <textarea
              value={widgetForm.description}
              onChange={(event) =>
                onWidgetFormChange({ description: event.target.value })
              }
              className="h-10 w-full resize-none rounded-lg border border-[#dbe4ef] bg-[#f8fafc] px-3 py-2 text-sm font-bold text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
            />
          </label>
          <div className="flex min-w-0 gap-3">
            <Button
              variant="primary"
              className="inline-flex min-h-[42px] w-full items-center gap-2 rounded-lg px-4 text-sm font-bold sm:w-auto md:min-w-[104px]"
              disabled={isSaving || isSaveDisabled}
              onClick={onSave}
            >
              {isSaving ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FiSave className="h-4 w-4" />
              )}
              ບັນທຶກ
            </Button>
          </div>
        </div>
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden p-2 sm:p-3">
        <FlowBuilder
          flowState={flowState}
          selectedNodeId={selectedFlowNodeId}
          config={config}
          onChange={onFlowChange}
          onSelectNode={onSelectFlowNode}
        />
      </div>
    </main>
  );
}
